import { useMemo, useState } from "react";
import { createSearchParams, useNavigate, useParams } from "react-router-dom";
import * as R from "ramda";
import { TFiltersData } from "components/Blog/Blog.types";
import { Button, Pill } from "shared";
import DateRangeModal from "./DateRangeModal";
import IconComponent from "shared/IconComponent";
import { IconName } from "shared/IconComponent/IconComponent";
import { useQueryParams } from "hooks";
import { BlogQueryParams } from "components/Blog/Blog.constants";
import { format } from "date-fns";
import SearchInput from "./SearchInput";
import { getFormattedDate } from "helpers";
import { isShowAllCategory } from "components/Blog/helpers";

type TBlogFiltersProps = {
  filtersData: TFiltersData;
  isTablet: boolean;
  defaultQueryParams: any;
  apiBaseUrl: string;
};

function BlogFilters({
  filtersData,
  isTablet,
  defaultQueryParams,
  apiBaseUrl,
}: TBlogFiltersProps) {
  const { queryParams, setQueryParams } = useQueryParams();
  const [isDateModalOpen, setIsDateModalOpen] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(
    (R.prop(BlogQueryParams.SearchTerm, queryParams) as string) || ""
  );
  const navigate = useNavigate();
  const { category } = useParams();
  const categoryList = filtersData.FilteringCategories;

  const realQueryParams = R.mergeLeft(queryParams, defaultQueryParams);

  const onClearAll = () => {
    navigate({
      pathname: "/",
      search: `?${createSearchParams({
        ...defaultQueryParams,
      })}`,
    });
    setInputValue("");
  };

  const onCategorySelection = (pathname: string) => {
    navigate({
      pathname,
      search: `?${createSearchParams({
        ...defaultQueryParams,
        [BlogQueryParams.PageSize]: R.prop(
          BlogQueryParams.PageSize,
          realQueryParams
        ),
        [BlogQueryParams.SortBy]: R.prop(
          BlogQueryParams.SortBy,
          realQueryParams
        ),
      })}`,
    });
    setInputValue("");
  };

  const showAllOption = useMemo(
    () => R.find((cat) => isShowAllCategory(cat), categoryList),
    [categoryList]
  );

  const renderSearchInput = () => (
    <SearchInput
      inputValue={inputValue}
      setInputValue={setInputValue}
      apiBaseUrl={apiBaseUrl}
      placeholder={filtersData.SearchPlaceholder}
      noOptionsMessage={filtersData.NoOptionsMessage}
      defaultQueryParams={defaultQueryParams}
    />
  );

  const renderFilterByDateButton = () => (
    <button
      className="o-blog-filters__btn"
      onClick={() => setIsDateModalOpen(true)}
    >
      <span>{filtersData.DateFilterText}</span>
      <IconComponent name={IconName.Calendar} />
    </button>
  );

  const renderClearAllFiltersButton = () => (
    <button className="o-blog-filters__btn" onClick={onClearAll}>
      <span>{filtersData.ClearFilterText}</span>
      <IconComponent name={IconName.Close} />
    </button>
  );

  const renderCategories = () => (
    <div className="o-blog-filters__categories-inner">
      {showAllOption && (
        <Pill
          key="show-all"
          onClick={() => onCategorySelection("/")}
          text={showAllOption.Title}
          isActive={!Boolean(category)}
        />
      )}
      {filtersData.FilteringCategories.map(
        (cat, idx) =>
          !isShowAllCategory(cat) && (
            <Pill
              key={`category-${cat.Id}-${idx}`}
              onClick={() =>
                onCategorySelection(
                  "/" + cat.Title.replaceAll(" ", "-").toLocaleLowerCase()
                )
              }
              text={cat.Title}
              isActive={
                category?.toLowerCase() === cat.Title.toLowerCase() ||
                category?.replaceAll("-", " ").toLowerCase() ===
                  cat.Title.toLowerCase()
              }
            />
          )
      )}
    </div>
  );

  return (
    <div className="o-blog-filters">
      <div className="o-blog-filters__content">
        {isTablet ? (
          <>
            {renderSearchInput()}
            <div className="o-blog-filters__row">
              <Button
                text={
                  isExpanded
                    ? filtersData.HideFilterText
                    : filtersData.ShowFiltersText
                }
                onClick={() => setIsExpanded(!isExpanded)}
                type="link"
                startIcon={<IconComponent name={IconName.DownArrow} />}
              />
              {renderClearAllFiltersButton()}
            </div>
            {isExpanded && (
              <div className="o-blog-filters__mobile-content">
                {renderCategories()}
                {renderFilterByDateButton()}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="o-blog-filters__categories">
              <div>{filtersData.FilterTagsText}</div>
              {renderCategories()}
            </div>
            {renderFilterByDateButton()}
            {renderClearAllFiltersButton()}
            {renderSearchInput()}
          </>
        )}
      </div>
      <DateRangeModal
        isOpen={isDateModalOpen}
        fromDate={getFormattedDate(
          R.prop(BlogQueryParams.FromDate, queryParams) as string
        )}
        toDate={getFormattedDate(
          R.prop(BlogQueryParams.ToDate, queryParams) as string
        )}
        onClose={() => setIsDateModalOpen(false)}
        onApply={(startDate, endDate) => {
          setInputValue("");
          setQueryParams({
            ...R.omit(
              [BlogQueryParams.FromDate, BlogQueryParams.ToDate],
              realQueryParams
            ),
            [BlogQueryParams.ActivePage]: 1,
            ...(startDate
              ? { [BlogQueryParams.FromDate]: format(startDate, "yyyy-MM-dd") }
              : {}),
            ...(endDate
              ? { [BlogQueryParams.ToDate]: format(endDate, "yyyy-MM-dd") }
              : {}),
          });
          setIsDateModalOpen(false);
        }}
        labels={{
          heading: filtersData.DateRange?.Heading,
          close: filtersData.DateRange?.Close,
          title: filtersData.DateRange?.Title,
          fromLabel: filtersData.DateRange?.FromLabel,
          fromPlaceholder: filtersData.DateRange?.FromPlaceholder,
          toLabel: filtersData.DateRange?.ToLabel,
          toPlaceholder: filtersData.DateRange?.ToPlaceholder,
          showResults: filtersData.DateRange?.ShowResults,
          clearFilters: filtersData.DateRange?.ClearFilters,
        }}
      />
    </div>
  );
}

export default BlogFilters;
